import { createAccessControl } from "better-auth/plugins/access";
import { defaultStatements, adminAc } from "better-auth/plugins/admin/access";

/**
 * All application resources and their allowed actions.
 *
 * `defaultStatements` ships with the admin plugin and includes:
 *   user:    ["create", "list", "set-role", "ban", "impersonate", "delete", "set-password"]
 *   session: ["list", "revoke", "delete"]
 *
 * Ownership scoping (own leads only, own tasks only, etc.) is NOT enforced
 * here — it is enforced at the data-access/query layer.
 */
const statement = {
  ...defaultStatements,
  lead: ["create", "read", "update", "delete"],
  task: ["create", "read", "update", "delete"],
  appointment: ["read"],
  tradeShow: ["create", "read", "update", "delete"],
  emailTemplate: ["create", "read", "update", "delete"],
  timeline: ["create", "read"],
  fileAttachment: ["create", "read"],
  notification: ["read", "update"],
  report: ["read"],
  settings: ["read", "update"],
} as const;

export const ac = createAccessControl(statement);

/**
 * Admin role — full access to all resources.
 * Inherits all default admin plugin permissions (user + session management)
 * plus unrestricted access to every application resource.
 */
export const adminRole = ac.newRole({
  ...adminAc.statements,
  lead: ["create", "read", "update", "delete"],
  task: ["create", "read", "update", "delete"],
  appointment: ["read"],
  tradeShow: ["create", "read", "update", "delete"],
  emailTemplate: ["create", "read", "update", "delete"],
  timeline: ["create", "read"],
  fileAttachment: ["create", "read"],
  notification: ["read", "update"],
  report: ["read"],
  settings: ["read", "update"],
});

/**
 * User role (Sales Team Member) — restricted access.
 * Permissions are resource-level; row-level scoping (own leads, own tasks,
 * own appointments) is enforced separately at the data-access layer.
 */
export const userRole = ac.newRole({
  lead: ["create", "read", "update", "delete"],
  task: ["create", "read", "update", "delete"],
  appointment: ["read"],
  tradeShow: ["create", "read", "update"], // no delete
  emailTemplate: ["read"],
  timeline: ["create", "read"],
  fileAttachment: ["create", "read"],
  notification: ["read", "update"],
  // report: no access
  settings: ["read"], // no update — timing rules are admin-only
});
